import { useEffect, useMemo, useState } from "react";
import { Save, FileText, RotateCcw, ExternalLink, Plus, X } from "lucide-react";
import api from "@/lib/api";
import { toast, Toaster } from "sonner";

// ====== Page configurations ======
const PAGE_TABS = [
  { id: "header", label: "Header (Üst Menü)", path: "/", endpoint: "/page-content/header", adminEndpoint: "/admin/page-content/header" },
  { id: "home", label: "Anasayfa", path: "/", endpoint: "/homepage-content", adminEndpoint: "/admin/homepage-content" },
  { id: "about", label: "Hakkımızda", path: "/hakkimizda", endpoint: "/page-content/about", adminEndpoint: "/admin/page-content/about" },
  { id: "products", label: "Ürünler", path: "/urunler", endpoint: "/page-content/products", adminEndpoint: "/admin/page-content/products" },
  { id: "sectors", label: "Sektörler", path: "/sektorler", endpoint: "/page-content/sectors", adminEndpoint: "/admin/page-content/sectors" },
  { id: "contact", label: "İletişim", path: "/iletisim", endpoint: "/page-content/contact", adminEndpoint: "/admin/page-content/contact" },
];

// Section schemas per page
const SECTIONS = {
  header: [
    {
      id: "topbar", title: "Üst Şerit (Top Bar)",
      fields: [
        { key: "topbar_left1", label: "Sol mesaj 1", hint: "Örn: ISO 9001 · ISO 14001 Sertifikalı Üretim" },
        { key: "topbar_left2", label: "Sol mesaj 2", hint: "Örn: 15+ Ülkeden Direkt İthalat" },
        { key: "topbar_admin_label", label: "Yönetici linki etiketi" },
      ],
    },
    {
      id: "nav", title: "Navigasyon Menüsü", twoCol: true,
      fields: [
        { key: "nav_home", label: "Anasayfa linki" },
        { key: "nav_about", label: "Hakkımızda linki" },
        { key: "nav_products", label: "Ürünler linki" },
        { key: "nav_sectors", label: "Sektörler linki" },
        { key: "nav_contact", label: "İletişim linki" },
      ],
    },
    {
      id: "cta", title: "Çağrı Butonu (CTA)",
      fields: [
        { key: "cta_label", label: "Buton metni", hint: "Sağ üstte görünen ana buton" },
      ],
    },
  ],
  home: [
    {
      id: "hero", title: "Hero (Üst Bölüm)",
      fields: [
        { key: "hero_badge", label: "Üst rozet" },
        { key: "hero_title_line1", label: "Başlık - 1. satır" },
        { key: "hero_title_line2", label: "Başlık - 2. satır" },
        { key: "hero_title_highlight", label: "Başlık - Mavi vurgu", hint: "Mavi renkte gösterilir" },
        { key: "hero_title_line4", label: "Başlık - 4. satır" },
        { key: "hero_subtitle", label: "Alt paragraf", multiline: true },
        { key: "hero_cta_primary", label: "Birincil buton" },
        { key: "hero_cta_secondary", label: "İkincil buton" },
      ],
    },
    {
      id: "stats", title: "İstatistikler", twoCol: true,
      fields: [
        { key: "stat1_value", label: "1. Değer" }, { key: "stat1_label", label: "1. Açıklama" },
        { key: "stat2_value", label: "2. Değer" }, { key: "stat2_label", label: "2. Açıklama" },
        { key: "stat3_value", label: "3. Değer" }, { key: "stat3_label", label: "3. Açıklama" },
        { key: "stat4_value", label: "4. Değer" }, { key: "stat4_label", label: "4. Açıklama" },
      ],
    },
    { id: "categories", title: "Kategoriler Bölümü", fields: [
      { key: "categories_eyebrow", label: "Üst etiket" },
      { key: "categories_title", label: "Başlık" },
    ]},
    { id: "featured", title: "Öne Çıkan Ürünler", fields: [
      { key: "featured_eyebrow", label: "Üst etiket" },
      { key: "featured_title", label: "Başlık" },
    ]},
    { id: "why", title: "Neden Biz Bölümü", fields: [
      { key: "why_eyebrow", label: "Üst etiket" },
      { key: "why_title", label: "Başlık" },
      { key: "why_description", label: "Açıklama", multiline: true },
    ]},
    { id: "process", title: "Süreç Bölümü", fields: [
      { key: "process_eyebrow", label: "Üst etiket" },
      { key: "process_title", label: "Başlık" },
    ]},
    { id: "cta", title: "Alt CTA", fields: [
      { key: "cta_title", label: "Başlık" },
      { key: "cta_description", label: "Açıklama", multiline: true },
    ]},
  ],
  about: [
    { id: "hero", title: "Hero", fields: [
      { key: "hero_eyebrow", label: "Üst etiket" },
      { key: "hero_title", label: "Başlık" },
      { key: "hero_subtitle", label: "Alt paragraf", multiline: true },
    ]},
    { id: "story", title: "Hikayemiz", fields: [
      { key: "story_eyebrow", label: "Üst etiket" },
      { key: "story_title", label: "Başlık" },
      { key: "story_p1", label: "Paragraf 1", multiline: true },
      { key: "story_p2", label: "Paragraf 2", multiline: true },
      { key: "story_p3", label: "Paragraf 3", multiline: true },
      { key: "story_image", label: "Hikaye görseli (URL)" },
    ]},
    { id: "stats", title: "İstatistikler", twoCol: true, fields: [
      { key: "stat1_v", label: "1. Değer" }, { key: "stat1_l", label: "1. Açıklama" },
      { key: "stat2_v", label: "2. Değer" }, { key: "stat2_l", label: "2. Açıklama" },
      { key: "stat3_v", label: "3. Değer" }, { key: "stat3_l", label: "3. Açıklama" },
    ]},
    { id: "values", title: "Değerlerimiz", twoCol: true, fields: [
      { key: "values_title", label: "Bölüm Başlığı", fullWidth: true },
      { key: "value1_title", label: "1. Değer Başlık" }, { key: "value1_desc", label: "1. Değer Açıklama" },
      { key: "value2_title", label: "2. Değer Başlık" }, { key: "value2_desc", label: "2. Değer Açıklama" },
      { key: "value3_title", label: "3. Değer Başlık" }, { key: "value3_desc", label: "3. Değer Açıklama" },
      { key: "value4_title", label: "4. Değer Başlık" }, { key: "value4_desc", label: "4. Değer Açıklama" },
    ]},
    { id: "certs", title: "Sertifikalar", fields: [
      { key: "cert_eyebrow", label: "Üst etiket" },
      { key: "cert_title", label: "Başlık" },
      { key: "cert_description", label: "Açıklama", multiline: true },
    ]},
    { id: "cta", title: "Alt CTA", fields: [
      { key: "cta_title", label: "Başlık" },
    ]},
  ],
  products: [
    { id: "hero", title: "Hero", fields: [
      { key: "hero_eyebrow", label: "Üst etiket" },
      { key: "hero_title", label: "Başlık" },
    ]},
  ],
  sectors: [
    { id: "hero", title: "Hero", fields: [
      { key: "hero_eyebrow", label: "Üst etiket" },
      { key: "hero_title_part1", label: "Başlık - Mavi vurgu" },
      { key: "hero_title_part2", label: "Başlık - Devamı" },
      { key: "hero_subtitle", label: "Alt paragraf", multiline: true },
    ]},
    { id: "sectors_list", title: "Sektör Listesi", custom: "sectors" },
  ],
  contact: [
    { id: "hero", title: "Hero", fields: [
      { key: "hero_eyebrow", label: "Üst etiket" },
      { key: "hero_title", label: "Başlık" },
    ]},
    { id: "info", title: "İletişim Bilgileri Bölümü", fields: [
      { key: "info_title", label: "Bölüm Başlığı" },
      { key: "working_hours", label: "Çalışma Saatleri" },
    ]},
    { id: "form", title: "Form Bölümü", fields: [
      { key: "form_title", label: "Form Başlığı" },
      { key: "form_subtitle", label: "Form Açıklaması", multiline: true },
    ]},
  ],
};

// ====== Sectors list editor ======
function SectorsListEditor({ value = [], onChange }) {
  const update = (i, val) => {
    const next = [...value];
    next[i] = val;
    onChange(next);
  };
  const add = () => onChange([...value, ""]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-2" data-testid="sectors-list-editor">
      <div className="flex justify-between items-center mb-3">
        <span className="text-xs uppercase tracking-wider text-slate-500">{value.length} sektör</span>
        <button onClick={add} type="button" className="inline-flex items-center gap-1 text-xs text-[#0066FF] hover:underline" data-testid="sectors-add">
          <Plus size={12} /> Sektör Ekle
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {value.map((s, i) => (
          <div key={i} className="flex gap-2">
            <span className="px-3 py-2 bg-slate-100 text-xs text-slate-500 flex items-center min-w-[2.5rem] justify-center">{i + 1}</span>
            <input type="text" value={s} onChange={(e) => update(i, e.target.value)} className="flex-1 px-3 py-2 border border-slate-200 focus:border-[#0066FF] outline-none text-sm" data-testid={`sector-input-${i}`} />
            <button onClick={() => remove(i)} type="button" className="px-2 text-red-600 hover:bg-red-50" data-testid={`sector-remove-${i}`}>
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ====== Main component ======
export default function AdminPages() {
  const [activeTab, setActiveTab] = useState("header");
  const [content, setContent] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState("hero");

  const tab = useMemo(() => PAGE_TABS.find((t) => t.id === activeTab), [activeTab]);
  const sections = SECTIONS[activeTab] || [];

  const load = async () => {
    if (!tab) return;
    const { data } = await api.get(tab.endpoint);
    setContent(data);
    setOriginal(data);
    setOpenSection(sections[0]?.id || "");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const update = (k) => (eOrVal) => {
    const val = eOrVal && eOrVal.target ? eOrVal.target.value : eOrVal;
    setContent((p) => ({ ...p, [k]: val }));
  };

  const dirty = content && original && JSON.stringify(content) !== JSON.stringify(original);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(tab.adminEndpoint, content);
      setContent(data);
      setOriginal(data);
      toast.success(`${tab.label} sayfası güncellendi`);
    } catch (e) {
      toast.error("Hata oluştu");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (!window.confirm("Tüm değişiklikleri geri almak istiyor musunuz?")) return;
    setContent(original);
  };

  return (
    <div data-testid="admin-pages-page" className="max-w-5xl pb-24">
      <Toaster position="top-right" />
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="heading-font text-3xl text-[#0A2540] flex items-center gap-3">
            <FileText size={28} className="text-[#0066FF]" />
            Sayfa İçerikleri
          </h1>
          <p className="text-sm text-slate-500 mt-1">Sitedeki her sayfanın metinlerini buradan düzenleyebilirsiniz.</p>
        </div>
        {tab && (
          <a
            href={tab.path}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-[#0066FF] hover:underline"
            data-testid="page-preview-link"
          >
            <ExternalLink size={14} /> Sayfayı Önizle
          </a>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-6 border-b border-slate-200" data-testid="page-tabs">
        {PAGE_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id
                ? "border-[#0066FF] text-[#0066FF]"
                : "border-transparent text-slate-600 hover:text-[#0A2540]"
            }`}
            data-testid={`page-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!content ? (
        <div className="text-slate-500 py-12 text-center">Yükleniyor...</div>
      ) : (
        <div className="space-y-3">
          {sections.map((section) => {
            const isOpen = openSection === section.id;
            return (
              <div key={section.id} className="bg-white border border-slate-200" data-testid={`section-${activeTab}-${section.id}`}>
                <button
                  type="button"
                  onClick={() => setOpenSection(isOpen ? "" : section.id)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                  data-testid={`section-toggle-${activeTab}-${section.id}`}
                >
                  <h2 className="heading-font text-lg text-[#0A2540]">{section.title}</h2>
                  <span className={`text-2xl text-slate-400 transition-transform ${isOpen ? "rotate-45" : ""}`}>+</span>
                </button>

                {isOpen && (
                  <div className="px-5 pb-5 pt-0 border-t border-slate-100">
                    <div className="mt-5">
                      {section.custom === "sectors" ? (
                        <SectorsListEditor
                          value={content.sectors || []}
                          onChange={(v) => setContent((p) => ({ ...p, sectors: v }))}
                        />
                      ) : (
                        <div className={`grid gap-4 ${section.twoCol ? "sm:grid-cols-2" : "grid-cols-1"}`}>
                          {section.fields?.map((f) => (
                            <div key={f.key} className={f.fullWidth ? "sm:col-span-2" : ""}>
                              <label className="block text-xs uppercase tracking-wider text-slate-500 mb-2">
                                {f.label}
                              </label>
                              {f.multiline ? (
                                <textarea
                                  value={content[f.key] || ""}
                                  onChange={update(f.key)}
                                  rows={4}
                                  className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none resize-none text-sm"
                                  data-testid={`field-${activeTab}-${f.key}`}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={content[f.key] || ""}
                                  onChange={update(f.key)}
                                  className="w-full px-4 py-3 border border-slate-200 focus:border-[#0066FF] outline-none text-sm"
                                  data-testid={`field-${activeTab}-${f.key}`}
                                />
                              )}
                              {f.hint && <div className="text-[10px] text-slate-400 mt-1">{f.hint}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky save bar */}
      <div className={`fixed bottom-0 left-0 lg:left-64 right-0 p-4 transition-all z-30 ${dirty ? "bg-[#0A2540]" : "bg-slate-100 border-t border-slate-200"}`}>
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className={`text-xs sm:text-sm ${dirty ? "text-white" : "text-slate-500"}`}>
            {dirty ? `${tab?.label} - kaydedilmemiş değişiklik` : "Tüm değişiklikler kaydedildi"}
          </div>
          <div className="flex gap-2">
            {dirty && (
              <button
                onClick={reset}
                type="button"
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/30 text-white hover:bg-white/10 text-sm"
                data-testid="page-reset"
              >
                <RotateCcw size={14} /> Geri Al
              </button>
            )}
            <button
              onClick={save}
              disabled={!dirty || saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0066FF] hover:bg-white hover:text-[#0066FF] text-white disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold transition-colors"
              data-testid="page-save"
            >
              <Save size={14} />
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
